import React from 'react'
import styled, { css } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import cn, { Argument as ClassName } from 'classnames'

interface ButtonGroupOption {
    value: unknown
    label: string
}

type ButtonGroupProps = {
    className?: ClassName
    value: unknown
    options: Array<ButtonGroupOption>
    onChange: (data: unknown) => void
    isUnselectable?: boolean
    withCheckmark?: boolean
    style?: React.CSSProperties
    dataTestId?: string
}

const SButtonGroup = styled.div`
    display: flex;
    z-index: 0;
`

const SButton = styled.button<{ active: boolean }>`
    display: inline-block;
    margin-bottom: 0;
    font-weight: 400;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    touch-action: manipulation;
    cursor: pointer;
    background-image: none;
    border: 1px solid transparent;
    padding: 6px 12px;
    font-size: 14px;
    line-height: 1.42857143;
    border-radius: 4px;
    user-select: none;
    min-height: 37px;
    color: #333;
    background-color: #fff;
    border-color: #ccc;

    :hover {
        ${({ active }) =>
            !active &&
            css`
                background-color: #e6e6e6;
                border-color: #adadad;
            `}
    }

    :not(:first-child) {
        margin-left: -1px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    :not(:last-child) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    ${(p) =>
        p.active &&
        css`
            color: ${({ theme }) => theme.font.darkestColor};
            background-color: ${({ theme }) => theme.brandColors.CA_Green};
            background-image: none;
            border-color: #adadad;
        `}
`

const SButtonGroupItem = styled(SButton)`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
`

const SIcon = styled(FontAwesomeIcon)`
    padding-left: 8px;
    color: ${({ theme }) => theme.font.darkestColor};
    font-size: 18px;
`

const ButtonGroup = ({
    value,
    options,
    onChange,
    dataTestId,
    isUnselectable,
    withCheckmark,
    style,
    className,
}: ButtonGroupProps): React.ReactElement => {
    const onClick = (newValue: unknown) => {
        if (isUnselectable && newValue === value) {
            onChange({ value: '' })
            return
        }
        onChange({ value: newValue })
    }

    return (
        <SButtonGroup className={cn('btn-group', 'TID-ButtonGroup', className)} style={style}>
            {options.map((option, i) => (
                <SButtonGroupItem
                    key={`firmwareType${option.value}`}
                    onClick={() => onClick(option.value)}
                    type="button"
                    active={value === option.value}
                    data-testid={'button-' + i}
                    dataTestId={dataTestId}
                >
                    {option.label}
                    {withCheckmark && value === option.value && <SIcon icon={faCheck} data-testid="checkmark" />}
                </SButtonGroupItem>
            ))}
        </SButtonGroup>
    )
}

export default ButtonGroup
